import { useEffect, useRef, useCallback } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.heat";
import type { SightingLocation } from "@/hooks/useSightingLocations";

declare module "leaflet" {
  function heatLayer(
    latlngs: [number, number, number][],
    options?: Record<string, unknown>
  ): L.Layer;
}

interface SightingsHeatmapProps {
  locations: SightingLocation[];
  highlightedId: string | null;
  onMarkerHover: (id: string | null) => void;
  onMarkerClick: (id: string) => void;
}

const SightingsHeatmap = ({ locations, highlightedId, onMarkerHover, onMarkerClick }: SightingsHeatmapProps) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<L.Map | null>(null);
  const markersRef = useRef<Map<string, L.CircleMarker>>(new Map());
  const heatLayerRef = useRef<L.Layer | null>(null);

  // Highlight effect
  useEffect(() => {
    markersRef.current.forEach((marker, id) => {
      if (id === highlightedId) {
        marker.setStyle({
          radius: 10,
          fillColor: "#f7b731",
          color: "#fc5c65",
          weight: 2,
          fillOpacity: 0.9,
        });
        marker.openPopup();
      } else {
        marker.setStyle({
          radius: 5,
          fillColor: "#2563eb",
          color: "#1d4ed8",
          weight: 1,
          fillOpacity: 0.6,
        });
        marker.closePopup();
      }
    });
  }, [highlightedId]);

  const handleMarkerHover = useCallback((id: string | null) => {
    onMarkerHover(id);
  }, [onMarkerHover]);

  // Initialize map once
  useEffect(() => {
    if (!mapRef.current || mapInstance.current) return;

    const map = L.map(mapRef.current, {
      center: [57.28, -4.48],
      zoom: 11,
      zoomControl: true,
      scrollWheelZoom: true,
    });
    mapInstance.current = map;

    L.tileLayer(
      "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
      {
        attribution: 'Tiles &copy; Esri &mdash; Source: Esri, Maxar, Earthstar Geographics',
        maxZoom: 19,
      }
    ).addTo(map);

    return () => {
      map.remove();
      mapInstance.current = null;
      markersRef.current.clear();
    };
  }, []);

  // Update markers & heat layer when locations change
  useEffect(() => {
    const map = mapInstance.current;
    if (!map || locations.length === 0) return;

    // Clear old markers
    markersRef.current.forEach((m) => m.remove());
    markersRef.current.clear();

    // Clear old heat layer
    if (heatLayerRef.current) {
      map.removeLayer(heatLayerRef.current);
      heatLayerRef.current = null;
    }

    // Add heat layer (deferred to avoid canvas height 0)
    const addHeatLayer = () => {
      const size = map.getSize();
      if (size.x === 0 || size.y === 0) return;
      const heatData: [number, number, number][] = locations.map((s) => [s.lat, s.lng, s.intensity]);
      heatLayerRef.current = L.heatLayer(heatData, {
        radius: 30, blur: 20, maxZoom: 15, max: 1.0,
        gradient: { 0.2: "#3b82f6", 0.4: "#06b6d4", 0.6: "#f59e0b", 0.8: "#f97316", 1.0: "#ef4444" },
      }).addTo(map);
      map.off("moveend", addHeatLayer);
    };

    setTimeout(() => {
      const size = map.getSize();
      if (size.x > 0 && size.y > 0) {
        addHeatLayer();
      } else {
        map.on("moveend", addHeatLayer);
      }
    }, 0);

    // Add markers
    locations.forEach((loc) => {
      const marker = L.circleMarker([loc.lat, loc.lng], {
        radius: 5,
        fillColor: "#2563eb",
        color: "#1d4ed8",
        weight: 1,
        opacity: 0.8,
        fillOpacity: 0.6,
      }).addTo(map);

      marker.bindPopup(
        `<div style="font-family: 'Source Sans 3', sans-serif; color: #1e293b; max-width: 220px;">
          <strong style="color: #2563eb; font-size: 14px;">${loc.label}</strong><br/>
          <span style="color: #d97706; font-size: 11px;">${loc.year}${loc.witness ? ` · ${loc.witness}` : ""}</span>
          <p style="color: #475569; font-size: 12px; margin-top: 6px; line-height: 1.4;">${loc.description.slice(0, 150)}${loc.description.length > 150 ? "…" : ""}</p>
        </div>`,
        { className: "dark-popup" }
      );

      marker.on("mouseover", () => handleMarkerHover(loc.id));
      marker.on("mouseout", () => handleMarkerHover(null));
      marker.on("click", () => onMarkerClick(loc.id));

      markersRef.current.set(loc.id, marker);
    });
  }, [locations, handleMarkerHover, onMarkerClick]);

  // Pan to highlighted marker
  useEffect(() => {
    if (!highlightedId || !mapInstance.current) return;
    const loc = locations.find((s) => s.id === highlightedId);
    if (loc) {
      mapInstance.current.panTo([loc.lat, loc.lng], { animate: true, duration: 0.4 });
    }
  }, [highlightedId, locations]);

  return (
    <div className="relative">
      <div ref={mapRef} className="w-full h-[400px] md:h-[calc(100vh-200px)] rounded-lg border border-border overflow-hidden" />
      <style>{`
        .dark-popup .leaflet-popup-content-wrapper {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          box-shadow: 0 10px 30px rgba(0,0,0,0.15);
        }
        .dark-popup .leaflet-popup-tip {
          background: #ffffff;
          border: 1px solid #e2e8f0;
        }
        .dark-popup .leaflet-popup-close-button { color: #64748b; }
      `}</style>
    </div>
  );
};

export default SightingsHeatmap;
